import type { DividerProps, SheetContentsProps } from '@hover/blueprint';
import {
  Center,
  Heading,
  Image,
  LoadingState,
  Sheet,
  Divider as SystemDivider,
  VStack,
  VisuallyHidden,
  useLatestCallback,
  useToast,
} from '@hover/blueprint';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useLogout } from 'src/modules/logout/hooks';
import { useHomeownerProfileQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { External, Route } from 'src/shared/route';
import { linkPressed } from 'src/shared/telemetry';

import { useHomeownerAppContext } from '../../hooks';
import type { MenuLinkProps } from '../MenuLink';
import { MenuLink } from '../MenuLink';
import { Skeleton } from '../Skeleton';

import { SheetFooter } from './SheetFooter';

export interface ProfileSheetProps extends SheetContentsProps {
  /**
   * Whether to display the profile photo
   *
   * @todo re-enable this after we've integrated social login
   */
  showProfilePicture?: boolean;
}

const Divider = (props: DividerProps) => (
  <SystemDivider borderColor="neutral.200" margin="0" size="small" {...props} />
);

const ProfileMenuLink = (props: MenuLinkProps) => (
  <MenuLink my="50 !important" width="100%" {...props} />
);

const ProfileSheet = ({
  showProfilePicture = false,
  ...props
}: ProfileSheetProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const history = useHistory();

  const toast = useToast();

  const { setCurrentMenu, loggedIn, trackingData } = useHomeownerAppContext();

  const [logout, { isPending: isLogoutPending }] = useLogout({
    redirect: false,
    onSuccess: () => {
      history.push(Route.HOMEOWNER_APP_HOME.path);
      setCurrentMenu(null);
    },
  });

  const { data, loading } = useHomeownerProfileQuery({
    onError: useLatestCallback(() => {
      if (!loggedIn || isLogoutPending) return;

      toast({
        status: 'error',
        description: t('profileSheet.anErrorOccurredLoadingProfile'),
      });
    }),
  });

  const { firstName, lastName, email, profilePictureUrl } =
    data?.profile.user ?? {};

  const profilePictureFallback =
    firstName && lastName ? `${firstName[0]}${lastName[0]}` : email?.[0] ?? '?';

  const editProfileText = t('profileSheet.editProfile');
  const logOutText = t('profileSheet.logOut');
  const privacyAndDataText = t('profileSheet.privacyAndData');

  return (
    <Sheet.Contents
      {...props}
      bodyProps={{
        display: 'flex',
        flexDirection: 'column',
        paddingTop: { base: '300', tablet: '900' },
        position: 'relative',
        px: '100',
        gap: '300',
        pb: '500',
      }}
      footer={<SheetFooter />}
      header={<VisuallyHidden>{t('profileSheet.title')}</VisuallyHidden>}
      sx={{ header: { marginBottom: '0' } }}
    >
      {showProfilePicture && (
        <Center
          bg="neutral.200"
          height="7rem"
          overflow="hidden"
          position="relative"
          rounded="round"
          width="7rem"
        >
          {!loading &&
            (profilePictureUrl ? (
              <Image
                alt={t('profileSheet.profilePhoto')}
                data-hj-suppress
                maxHeight="100%"
                maxWidth="100%"
                src={profilePictureUrl}
              />
            ) : (
              <Heading
                as="span"
                color="neutral.400"
                isLight
                marginBottom="0"
                size={600}
                textTransform="uppercase"
              >
                {profilePictureFallback}
              </Heading>
            ))}
          <LoadingState color="neutral.400" isLoading={loading} size="large" />
        </Center>
      )}
      <Skeleton isLoaded={!loading} rounded="200">
        <Heading
          as="h1"
          data-hj-suppress
          lineHeight="600"
          mb="200"
          mt="100"
          size={700}
        >
          {t('profileSheet.hi', {
            firstName: firstName ?? t('profileSheet.homeowner'),
          })}
        </Heading>
      </Skeleton>
      <VStack gap="500" marginBottom={500} spacing="0">
        <Divider />
        <ProfileMenuLink
          buttonText={editProfileText}
          href={External.SETTINGS.path}
          onClick={() => {
            linkPressed({
              ...trackingData,
              page_or_screen_name: trackingData?.page_or_screen_name ?? '',
              link_text: editProfileText,
              link_location: 'profile-menu',
              primary_cta: false,
              track_id: 'edit-profile-link',
            });
          }}
          target="_blank"
        >
          {editProfileText}
        </ProfileMenuLink>
        <Divider />
        <ProfileMenuLink
          buttonText={privacyAndDataText}
          href={External.PRIVACY_POLICY.path}
          onClick={() => {
            linkPressed({
              ...trackingData,
              page_or_screen_name: trackingData?.page_or_screen_name ?? '',
              link_text: privacyAndDataText,
              link_location: 'profile-menu',
              primary_cta: false,
              track_id: 'open-privacy-and-data-policy-link',
            });
          }}
          target="_blank"
        >
          {privacyAndDataText}
        </ProfileMenuLink>
        <Divider />
        <ProfileMenuLink
          as="button"
          buttonText={logOutText}
          cursor="pointer"
          isLoading={isLogoutPending}
          onClick={async () => {
            linkPressed({
              ...trackingData,
              page_or_screen_name: trackingData?.page_or_screen_name ?? '',
              link_text: logOutText,
              link_location: 'profile-menu',
              primary_cta: false,
              track_id: 'log-out-link',
            });
            await logout();
          }}
        >
          {logOutText}
        </ProfileMenuLink>
        <Divider />
      </VStack>
    </Sheet.Contents>
  );
};

export { ProfileSheet };
